import { secureRandom } from '@/lib/secureRandom';
import { useState, useEffect } from 'react';
import { useFinopsStore } from '../../../store/finopsStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Plus, Search, Cloud, BarChart3, TrendingDown } from 'lucide-react';
import { Input } from '../../../components/Input';
import toast from 'react-hot-toast';

export function FinOps() {
    const { costs, isLoading, fetchCosts, createCost } = useFinopsStore();
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchCosts();
    }, []);

    const filteredCosts = costs.filter(c => 
        c.provider.toLowerCase().includes(search.toLowerCase()) ||
        c.serviceName.toLowerCase().includes(search.toLowerCase())
    );

    const formatCurrency = (val: number, currency = 'USD') => 
        new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(val);

    const handleCreateMock = async () => {
        try {
            await createCost({
                provider: 'AWS',
                serviceName: 'EC2 Instances',
                amount: Math.floor(secureRandom() * 1000) + 100,
                currency: 'USD',
                status: 'processed',
                billingPeriod: new Date().toISOString(),
            });
            toast.success('Cost recorded');
        } catch (e) {
            toast.error('Failed to record cost');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">FinOps (Cloud Costs)</h1>
                    <p className="text-muted-foreground">Monitor and optimize cloud infrastructure spending.</p>
                </div>
                <Button variant="primary" onClick={handleCreateMock}>
                    <Plus className="w-4 h-4 mr-2" />
                    Record Cost
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                            <Cloud className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Total Cloud Spend</p>
                            <h3 className="text-foreground font-display text-metric">
                                {formatCurrency(costs.reduce((acc, c) => acc + c.amount, 0))}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
                            <TrendingDown className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Est. Savings</p>
                            <h3 className="text-foreground font-display text-metric">
                                {formatCurrency(costs.reduce((acc, c) => acc + c.amount, 0) * 0.15)}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
                            <BarChart3 className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Active Services</p>
                            <h3 className="text-foreground font-display text-metric">
                                {new Set(costs.map(c => c.serviceName)).size}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <h3 className="text-foreground font-display text-section-title">Cost Breakdown</h3>
                        <div className="relative w-64">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input 
                                placeholder="Search provider or service..." 
                                className="pl-9"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : (
                        <table className="w-full text-sm text-left">
                            <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                                <tr>
                                    <th className="px-6 py-3">Provider</th>
                                    <th className="px-6 py-3">Service</th>
                                    <th className="px-6 py-3">Amount</th>
                                    <th className="px-6 py-3">Period</th>
                                    <th className="px-6 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredCosts.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">
                                            No cloud costs found.
                                        </td>
                                    </tr>
                                ) : filteredCosts.map(c => (
                                    <tr key={c.id} className="hover:bg-secondary/20 transition-colors">
                                        <td className="px-6 py-4 font-medium text-foreground">{c.provider}</td>
                                        <td className="px-6 py-4">{c.serviceName}</td>
                                        <td className="px-6 py-4">{formatCurrency(c.amount, c.currency)}</td>
                                        <td className="px-6 py-4">
                                            {c.billingPeriod ? new Date(c.billingPeriod).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant={c.status === 'processed' ? 'success' : 'secondary'}>
                                                {c.status.toUpperCase()}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
